import { Injectable } from '@angular/core';
import { ApplicantAttachment } from '@app/models/recruiting/applicant-attachments.model';
import { ApplicantDiscussion } from '@app/models/recruiting/applicant-discussion.model';
import { JobAttachment } from '@app/models/recruiting/job-attachments.model';
import { AuthService } from '@app/services';
import { Applicant } from '@models/recruiting/applicant.model';
import { ReplaySubject } from 'rxjs';

@Injectable()
export class ApplicantProfileService {
    applicantSubject = new ReplaySubject<Applicant>();

    applicantAttachments: ApplicantAttachment[];
    jobAttachments: JobAttachment[];
    applicantDiscussions: ApplicantDiscussion[];
    private _applicant: Applicant;

    constructor(private auth: AuthService) {}

    set applicant(applicant: Applicant) {
        this._applicant = applicant;
        this.applicantSubject.next(this._applicant);
    }

    async refreshApplicant(): Promise<void> {
        this._applicant = await Applicant.param('company', this.auth.company.id)
            .with(['jobPosting.hiringTeam', 'jobPosting.jobFunnels', 'jobFunnel', 'connectEmployeeLink'])
            .find(this._applicant.id);

        this.applicantSubject.next(this._applicant);
    }

    async getApplicantAttachments(applicantId: number): Promise<ApplicantAttachment[]> {
        if (this.applicantAttachments) {
            return this.applicantAttachments;
        }

        const [applicantAttachments] = await ApplicantAttachment.where('applicantId', applicantId)
            .with(['jobAttachment', 'file'])
            .orderBy('jobAttachmentIdAndCreatedAt', 'DESC')
            .get();

        this.applicantAttachments = applicantAttachments;
        return applicantAttachments;
    }

    async getJobAttachments(jobPostingId: number): Promise<JobAttachment[]> {
        if (this.jobAttachments) {
            return this.jobAttachments;
        }

        const [jobAttachments] = await JobAttachment.with(['jobAttachmentType']).where('atsJobId', jobPostingId).get();

        this.jobAttachments = jobAttachments;

        return jobAttachments;
    }

    async getDiscussions(applicant: Applicant): Promise<ApplicantDiscussion[]> {
        if (this.applicantDiscussions) {
            return this.applicantDiscussions;
        }

        const [applicantDiscussions] = await ApplicantDiscussion.with(['comments.author', 'comments.taggedEmployees'])
            .where('atsApplicantId', applicant.id)
            .get();

        this.applicantDiscussions = applicantDiscussions;

        return applicantDiscussions;
    }

    clearJobAttachments(): void {
        this.jobAttachments = null;
    }

    clearApplicantAttachments(): void {
        this.applicantAttachments = null;
    }

    clearApplicantDiscussion(): void {
        this.applicantDiscussions = null;
    }

    clearAllCaches(): void {
        this.clearJobAttachments();
        this.clearApplicantAttachments();
        this.clearApplicantDiscussion();
    }
}
